import { useSelector } from 'react-redux';

import type { PartFileStandaloneViewerData } from '@frontend/api';
import {
  Button,
  Divider,
  DownloadDiskIcon,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  PencilBarIcon,
  Portal,
  Text,
  Tooltip,
  TruncateTextWithTooltip,
  useDisclosure,
  useBreakpointValue,
  HStack,
} from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import { DOWNLOAD_PART_FILES_PERMISSIONS } from 'constants/all';

import { HeaderSuggestionsContent } from 'modules/fileViewer/components/HeaderSuggestionsContent';

import {
  getGroupMemberSupplierRoles,
  getIsSupplierDdtcRegistered,
  isSuperUserOrGroupAdmin,
} from 'store/session/selectors';

import { getActiveRootFile } from '../data/reducer';

const ExportControlledBanner = () => {
  return (
    <Flex justify="center" align="center" bg="status.onHold" height="28px">
      <Text casing="uppercase" color="page.surface" size="sm" fontWeight="bold">
        Export Controlled (ITAR)
      </Text>
    </Flex>
  );
};

const FileName = ({ filename }: { filename?: string | null }) => {
  return (
    <TruncateTextWithTooltip
      text={filename ?? '---'}
      size="lg"
      fontWeight="bold"
      flex="1 0 auto"
      fontFamily="default"
      maxWidth="30em"
    />
  );
};

const FileDataBlock = ({
  title,
  value,
  nowrap = false,
}: {
  title: string;
  value: string | null;
  nowrap?: boolean;
}) => {
  return (
    <Flex
      ml={2}
      direction="column"
      color="feature.secondary"
      wordBreak="break-word"
    >
      <Text
        casing="uppercase"
        color="feature.secondary"
        size="sm"
        fontWeight="bold"
        whiteSpace="nowrap"
      >
        {title}
      </Text>
      <Text whiteSpace={nowrap ? 'nowrap' : 'normal'} overflow="hidden">
        {value ?? '---'}
      </Text>
    </Flex>
  );
};

type FileDataProps = {
  activeFile: PartFileStandaloneViewerData;
  isMobile?: boolean;
};

const FileData = ({ activeFile, isMobile = false }: FileDataProps) => {
  const { originalFileSize, created, uploadedBy } = activeFile;
  const { toDateTimeString } = useDateFormat();

  return (
    <Flex pt={isMobile ? 0.5 : 0} pb={isMobile ? 1 : 0}>
      <FileDataBlock
        title="File Size"
        nowrap
        value={
          originalFileSize
            ? (
                Math.round((originalFileSize / (1024 * 1024)) * 100) / 100
              ).toString() + ' MB'
            : null
        }
      />
      <FileDataBlock
        title="Date Uploaded"
        nowrap
        value={created ? toDateTimeString(created) : null}
      />
      <FileDataBlock title="Uploaded By" value={uploadedBy ?? null} nowrap />
    </Flex>
  );
};

type ActionsMenuProps = {
  activeFile: PartFileStandaloneViewerData;
  itarPermissions: boolean;
  downloadPermissions: boolean;
  onOpenItarModal: () => void;
};

const ActionsMenu = ({
  activeFile,
  itarPermissions,
  downloadPermissions,
  onOpenItarModal,
}: ActionsMenuProps) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const { containsVirus, authenticatedDownloadUrl } = activeFile;

  return (
    <Flex height="100%" align="center">
      {itarPermissions ? (
        <Menu isOpen={isOpen} onClose={onClose}>
          <MenuButton aria-label="Quote Actions" onClick={onToggle}>
            Actions
          </MenuButton>
          <Portal>
            <MenuList>
              <Tooltip
                label={
                  containsVirus
                    ? 'Cannot download a file with malware. Consider deleting this file'
                    : !downloadPermissions
                    ? 'You do not have permission to download files.'
                    : ''
                }
              >
                <MenuItem
                  isDisabled={containsVirus || !downloadPermissions}
                  as="a"
                  href={authenticatedDownloadUrl}
                  target="_blank"
                  icon={<DownloadDiskIcon />}
                >
                  Download file
                </MenuItem>
              </Tooltip>
              <MenuItem onClick={onOpenItarModal} icon={<PencilBarIcon />}>
                Edit ITAR status
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      ) : (
        <Tooltip
          label={
            containsVirus
              ? 'Cannot download a file with malware. Consider deleting this file'
              : !downloadPermissions
              ? 'You do not have permission to download files.'
              : ''
          }
        >
          <Button
            isDisabled={containsVirus || !downloadPermissions}
            as="a"
            href={authenticatedDownloadUrl}
            target="_blank"
            rightIcon={<DownloadDiskIcon />}
            variant="secondary"
          >
            Download File
          </Button>
        </Tooltip>
      )}
    </Flex>
  );
};

type FileViewerHeaderProps = {
  onOpen: () => void;
};

export const FileViewerHeader = ({ onOpen }: FileViewerHeaderProps) => {
  const isMobile = useBreakpointValue(
    {
      base: true,
      tablet: true,
      desktop: false,
    },
    { ssr: false },
  );

  const activeFile = useSelector(getActiveRootFile);
  const { isExportControlled, filename } = activeFile ?? {};

  // TODO: update activeFile in redux following change of ITAR classification
  // Get ITAR permissions
  const isSupplierDdtcRegistered = useSelector(getIsSupplierDdtcRegistered);
  // Get Download permissions
  const groupMemberWithDownloadPermission =
    useSelector(getGroupMemberSupplierRoles).downloadPartFiles ===
    DOWNLOAD_PART_FILES_PERMISSIONS.ACCESS;
  const superUserOrGroupAdmin = useSelector(isSuperUserOrGroupAdmin);
  const userHasPermissionToDownload =
    groupMemberWithDownloadPermission || superUserOrGroupAdmin;

  if (!activeFile) return <></>;

  return (
    <>
      {isExportControlled && <ExportControlledBanner />}
      {isMobile ? (
        <Flex
          direction="column"
          justify="space-around"
          bg="page.surface"
          borderBottom="1px solid"
          borderColor="separator.border"
        >
          <Flex my={1}>
            <Flex height="100%" align="center" px={2} flex="1" minWidth={0}>
              <FileName filename={filename} />
            </Flex>
            <Flex height="100%" align="center" flexShrink={0}>
              <ActionsMenu
                activeFile={activeFile}
                itarPermissions={isSupplierDdtcRegistered}
                downloadPermissions={userHasPermissionToDownload}
                onOpenItarModal={onOpen}
              />
            </Flex>
          </Flex>
          <Flex mb={1} overflowX="auto">
            <FileData activeFile={activeFile} isMobile />
            <HeaderSuggestionsContent activeFile={activeFile} isMobile />
          </Flex>
        </Flex>
      ) : (
        <HStack
          justify="space-between"
          bg="page.surface"
          borderBottom="1px solid"
          borderColor="separator.border"
          spacing={0}
          pr={2}
        >
          <HStack
            align="center"
            justify="start"
            flex="1"
            minWidth={0}
            overflowX="auto"
            px={2}
            py={1}
          >
            <FileName filename={filename} />
            <Divider orientation="vertical" ml={2} />
            <FileData activeFile={activeFile} />
            <HeaderSuggestionsContent activeFile={activeFile} />
          </HStack>
          <Divider orientation="vertical" mr={2} />
          <ActionsMenu
            activeFile={activeFile}
            itarPermissions={isSupplierDdtcRegistered}
            downloadPermissions={userHasPermissionToDownload}
            onOpenItarModal={onOpen}
          />
        </HStack>
      )}
    </>
  );
};
